/**
 * Infinite Scroll — Prestige Unified Collection
 * Works with main-collection--unified.liquid + product-card--unified.liquid
 */

(function () {
  'use strict';

  // ─── State ────────────────────────────────────────────────────────────────
  let currentPage = 1;
  let isFetching  = false;
  let hasMore     = true;
  let observer    = null;
  let seenHandles = new Set();
  let spinnerEl   = null;
  let initToken   = 0; // incremented each init() call to invalidate stale async work

  // ─── Helpers ──────────────────────────────────────────────────────────────
  function getProductList() {
    return document.querySelector('product-list.product-list');
  }

  function handleFromCard(card) {
    const attr = card.getAttribute('handle');
    if (attr) return attr;
    const a = card.querySelector('a[href*="/products/"]');
    if (a) {
      const m = a.href.match(/\/products\/([^/?#]+)/);
      if (m) return m[1];
    }
    return null;
  }

  function seedSeen(productList) {
    seenHandles = new Set();
    productList.querySelectorAll('product-card').forEach(card => {
      const h = handleFromCard(card);
      if (h) seenHandles.add(h);
    });
  }

  function getSectionId() {
    const sec = document.querySelector('.shopify-section--main-collection');
    if (sec) {
      const wrapper = sec.closest('[id^="shopify-section-"]');
      if (wrapper) return wrapper.id.replace('shopify-section-', '');
    }
    const any = document.querySelector('[id^="shopify-section-"]');
    if (any) return any.id.replace('shopify-section-', '');
    return 'main-collection--unified';
  }

  function nextPageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);
    url.searchParams.set('section_id', getSectionId());
    return url.toString();
  }

  // ─── Spinner ──────────────────────────────────────────────────────────────
  function buildSpinner() {
    const wrap = document.createElement('div');
    wrap.id = 'is-spinner';
    wrap.setAttribute('role', 'status');
    wrap.setAttribute('aria-label', 'Loading more products');
    wrap.style.cssText = 'display:none;justify-content:center;align-items:center;padding:40px 0 24px;width:100%;';
    wrap.innerHTML =
      '<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" ' +
      'style="animation:__is_spin .75s linear infinite;color:var(--color-foreground,#111)">' +
        '<circle cx="16" cy="16" r="13" stroke="currentColor" stroke-width="2.5" stroke-dasharray="62 20" stroke-linecap="round" opacity=".2"/>' +
        '<path d="M16 3 a13 13 0 0 1 13 13" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>' +
      '</svg>';
    if (!document.getElementById('__is_keyframes')) {
      const s = document.createElement('style');
      s.id = '__is_keyframes';
      s.textContent = '@keyframes __is_spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}';
      document.head.appendChild(s);
    }
    return wrap;
  }

  function showSpinner() { if (spinnerEl) spinnerEl.style.display = 'flex'; }
  function hideSpinner() { if (spinnerEl) spinnerEl.style.display = 'none'; }

  // ─── Core fetch ───────────────────────────────────────────────────────────
  async function loadNextPage() {
    if (isFetching || !hasMore) return;

    const productList = getProductList();
    if (!productList) return;

    isFetching = true;
    showSpinner();

    try {
      currentPage += 1;

      const res = await fetch(nextPageUrl(currentPage), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!res.ok) {
        hasMore = false;
        return;
      }

      const html = await res.text();
      const doc  = new DOMParser().parseFromString(html, 'text/html');

      const remoteList =
        doc.querySelector('product-list.product-list') ||
        doc.querySelector('product-list');

      if (!remoteList) {
        hasMore = false;
        return;
      }

      const incoming = Array.from(remoteList.children).filter(
        el => el.tagName.toLowerCase() === 'product-card'
      );

      if (incoming.length === 0) {
        hasMore = false;
        return;
      }

      let appended = 0;
      incoming.forEach(card => {
        const h = handleFromCard(card);
        if (h && seenHandles.has(h)) return;
        if (h) seenHandles.add(h);
        productList.appendChild(document.adoptNode(card));
        appended++;
        if (typeof window.initUnifiedCard === 'function') {
          window.initUnifiedCard(card);
        }
      });

      if (appended === 0) {
        hasMore = false;
        return;
      }

      const hasNextPage =
        !!doc.querySelector('a[rel="next"]') ||
        !!doc.querySelector('.pagination__next') ||
        !!doc.querySelector('[data-pagination-next]');

      if (!hasNextPage) {
        hasMore = false;
      }

      if (hasMore) {
        attachObserver(productList);
      }

    } catch (err) {
      console.warn('[InfiniteScroll] fetch error:', err);
      currentPage -= 1;
    } finally {
      isFetching = false;
      hideSpinner();
    }
  }

  // ─── Observer ─────────────────────────────────────────────────────────────
  // Watch the 20th-from-last card (5 rows at 4 cols) so the next fetch fires
  // well before the user reaches the end of the loaded cards.
  const TRIGGER_FROM_END = 20;

  function attachObserver(productList) {
    if (observer) observer.disconnect();

    const cards = productList.querySelectorAll('product-card');
    if (cards.length === 0) return;

    const triggerIdx  = Math.max(0, cards.length - TRIGGER_FROM_END);
    const triggerCard = cards[triggerIdx];

    observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadNextPage();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );

    observer.observe(triggerCard);

    // Safety net: if the trigger card is ALREADY above/at the viewport edge
    // by the time we attach (e.g. user scrolled fast, or init() ran late
    // after a full page reload where the user was already scrolled down),
    // the IntersectionObserver may not fire a callback because there's no
    // new intersection change to report. Manually check and fetch if needed.
    const myToken = initToken;
    requestAnimationFrame(() => {
      // Abandon if a newer init() has run since this observer was attached
      if (myToken !== initToken) return;
      const rect = triggerCard.getBoundingClientRect();
      const alreadyPastTrigger = rect.bottom < window.innerHeight + 400;
      if (alreadyPastTrigger && !isFetching && hasMore) {
        loadNextPage();
      }
    });
  }

  // ─── Init / teardown ──────────────────────────────────────────────────────
  function teardown() {
    if (observer) { observer.disconnect(); observer = null; }
    document.getElementById('is-spinner')?.remove();
    spinnerEl = null;
    isFetching = false; // release any stuck fetch lock from an abandoned run
  }

  function init() {
    initToken++;
    const myToken = initToken;

    teardown();

    const productList = getProductList();
    if (!productList) return;

    // If another init() call started after this one began, abandon this run
    if (myToken !== initToken) return;

    currentPage = 1;
    isFetching  = false;
    hasMore     = true;
    seedSeen(productList);

    // Hide native pagination
    document.querySelectorAll('.pagination, [data-pagination]').forEach(el => {
      el.style.display = 'none';
    });

    // If fewer than 8 cards and no pagination element exists, it's a single page
    const paginationExists =
      !!document.querySelector('.pagination') ||
      !!document.querySelector('[data-pagination]') ||
      !!document.querySelector('a[rel="next"]') ||
      !!document.querySelector('.pagination__next');
    const cardCount = productList.querySelectorAll('product-card').length;
    if (!paginationExists && cardCount < 8) {
      hasMore = false;
      return;
    }

    // Spinner sits after .collection, outside all CSS grid contexts
    spinnerEl = buildSpinner();
    const collectionEl   = productList.closest('.collection') || productList.parentNode;
    const insertionParent = collectionEl.parentNode;
    insertionParent.insertBefore(spinnerEl, collectionEl.nextSibling);

    attachObserver(productList);

    // Settle-check: if Prestige is still mid-transition when init() ran,
    // the card count we just seeded against may be stale. Re-check shortly
    // after and re-init if the count has changed since.
    const countAtInit = productList.querySelectorAll('product-card').length;
    setTimeout(() => {
      if (myToken !== initToken) return; // a newer init already superseded this
      const pl2 = getProductList();
      if (!pl2) return;
      const countNow = pl2.querySelectorAll('product-card').length;
      if (countNow !== countAtInit) {
        reinit();
      }
    }, 400);
  }

  // ─── Filter / sort change detection ───────────────────────────────────────
  function reinit() {
    requestAnimationFrame(() => setTimeout(init, 80));
  }

  function listenForReset() {
    // Prestige replaces the ENTIRE inner content of the section on filter —
    // it removes the whole `.color-scheme` wrapper div (which contains
    // .container > .v-stack > .collection > .collection__main > product-list)
    // and inserts a brand new one. This means product-list itself gets
    // destroyed and recreated, so watching product-list directly never works
    // — by the time any observer on it could fire, it's already gone.
    //
    // The only stable, never-replaced ancestor is the <section> element
    // itself (id="shopify-section-..."). We watch ITS direct children for
    // any change at all, which reliably fires exactly once per filter/sort.
    let debounceTimer = null;

    function findSectionEl() {
      return document.querySelector('.shopify-section--main-collection');
    }

    function watchSection() {
      const sectionEl = findSectionEl();
      if (!sectionEl) {
        setTimeout(watchSection, 150);
        return;
      }

      new MutationObserver(() => {
        if (isFetching) return; // ignore mutations from our own appendChild calls

        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          reinit();
        }, 150);
      }).observe(sectionEl, { childList: true });
    }

    watchSection();

    // Keep these as fallbacks in case other filter/sort interactions
    // (sidebar AJAX, programmatic navigation) DO fire one of these.
    document.addEventListener('shopify:section:load', reinit);
    document.addEventListener('collection:filter:updated', reinit);
    window.addEventListener('popstate', reinit);
    window.addEventListener('pageshow', (e) => { if (e.persisted) reinit(); });
  }

  // ─── Debug hook ───────────────────────────────────────────────────────────
  // Call window.__infiniteScrollDebug() in the console at any time to inspect
  // the script's internal state without needing closure access.
  window.__infiniteScrollDebug = function () {
    const pl = getProductList();
    const cards = pl ? pl.querySelectorAll('product-card') : [];
    const info = {
      currentPage,
      isFetching,
      hasMore,
      observerAttached: !!observer,
      cardCountInDOM: cards.length,
      seenHandlesCount: seenHandles.size,
      spinnerExists: !!document.getElementById('is-spinner'),
      spinnerVisible: spinnerEl ? spinnerEl.style.display === 'flex' : null,
      triggerCardIndex: cards.length ? Math.max(0, cards.length - TRIGGER_FROM_END) : null,
      triggerCardHandle: cards.length ? handleFromCard(cards[Math.max(0, cards.length - TRIGGER_FROM_END)]) : null,
    };
    console.table(info);
    return info;
  };

  // Manually force the next page to load, bypassing the observer entirely.
  // Useful for testing: window.__infiniteScrollForceLoad()
  window.__infiniteScrollForceLoad = function () {
    console.log('[InfiniteScroll] forcing loadNextPage()...');
    loadNextPage();
  };

  // ─── Boot ─────────────────────────────────────────────────────────────────
  function boot() {
    init();
    listenForReset();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
